/*

PW Text Overflow Handling

This component is a handy shortcut for displaying a long string of text truncated with
ellipsis if needed, and automatically expanding the full text out as an overlay when hovered.
The overlay matches the styling and position of the truncated text exactly, so it appears to
the user as if the clipped part of the text is "pulled up" to the top layer. It will also
automatically reposition itself if necessary to keep the overlay within the viewport.

Basic usage:

  <TextOverflow className="desc">This is some very long text that will be truncated</TextOverflow>

This will create a `div` containing that text, with the overflow ellipsis styling applied,
and the hover behavior attached. Any `props` will be copied to the `div` element.

Advanced options:

  <TextOverflow tagName="h1" delay={1000} watchMove overlayClass="special">...</TextOverflow>

- tagName: Specifies the tag to use for the rendered element. Defaults to "div".
- delay: Specifies how long after hovering to wait to show the overlay. Defaults to 500 ms.
- watchMove: If set, the main element's position will be polled and if it moves then the
             overlay will be closed, to prevent misalignment.
- overlayClass: A special className to be added to the overlay element, for custom styling.

*/

import _ from 'lodash'
import React from 'react'
import T from 'prop-types'
import Timing from '../hoc/Timing'
import ReactDOM from 'react-dom'
import TextOverflowOverlay from './TextOverflowOverlay'

const GLOBAL_HIDE_EVENTS = ['mousewheel', 'mousedown']

class TextOverflow extends React.Component {
  static displayName = 'TextOverflow'

  static propTypes = {
    tagName: T.string,
    delay: T.number,
    watchMove: T.bool,
    overlayClass: T.string,
    onMouseEnter: T.func,
    onMouseLeave: T.func,

    setTimer: T.func.isRequired,
    clearTimer: T.func.isRequired
  }

  static defaultProps = {
    tagName: 'div',
    delay: 500,
    watchMove: false
  }

  componentWillUnmount() {
    this._destroyOverlay()
  }

  _onMouseEnter = e => {
    const { onMouseEnter, delay } = this.props
    if (onMouseEnter) {
      onMouseEnter(e)
    }
    if (delay) {
      this.props.setTimer(this._checkOverflow, delay, 'delay')
    } else {
      this._checkOverflow()
    }
  }

  _onMouseLeave = e => {
    const { onMouseLeave } = this.props
    if (onMouseLeave) {
      onMouseLeave(e)
    }
    this._destroyOverlay()
  }

  _checkOverflow = () => {
    const { clientWidth, scrollWidth } = ReactDOM.findDOMNode(this)
    if (scrollWidth > clientWidth) {
      this._showOverlay()
    }
  }

  _watchMove = () => {
    const box = ReactDOM.findDOMNode(this).getBoundingClientRect()
    const origBox = this._origBBox
    if (origBox) {
      if (
        box.left !== origBox.left ||
        box.top !== origBox.top ||
        box.right !== origBox.right ||
        box.bottom !== origBox.bottom
      ) {
        this._destroyOverlay()
        return
      }
    } else {
      this._origBBox = box
    }
    this.props.setTimer(this._watchMove, 50, 'watchMove')
  }

  _showOverlay = () => {
    this._destroyOverlay() //just in case
    const el = ReactDOM.findDOMNode(this)

    const mountNode = (this._overlayMountNode = document.createElement('div'))
    mountNode.className = 'text_overflow_overlay_mount'
    document.body.appendChild(mountNode)

    ReactDOM.render(
      <TextOverflowOverlay source={el} className={this.props.overlayClass} />,
      mountNode
    )
    GLOBAL_HIDE_EVENTS.forEach(name =>
      window.addEventListener(name, this._destroyOverlay, true)
    )

    if (this.props.watchMove) {
      this._watchMove()
    }
  }

  _destroyOverlay = () => {
    const mountNode = this._overlayMountNode
    if (mountNode && mountNode.parentNode) {
      ReactDOM.unmountComponentAtNode(mountNode)
      mountNode.parentNode.removeChild(mountNode)
      delete this._overlayMountNode
    }
    GLOBAL_HIDE_EVENTS.forEach(name =>
      window.removeEventListener(name, this._destroyOverlay, true)
    )
    this.props.clearTimer('delay')
    this.props.clearTimer('watchMove')
    delete this._origBBox
  }

  render() {
    const { props } = this
    const subProps = _.omit(
      props,
      'tagName',
      'onMouseEnter',
      'onMouseLeave',
      'delay',
      'watchMove',
      'overlayClass',
      'clearTimer',
      'setTimer',
      'getRef'
    )
    const Comp = props.tagName
    return (
      <Comp
        {...subProps}
        onMouseEnter={this._onMouseEnter}
        onTouchStart={this._onMouseEnter}
        onMouseLeave={this._onMouseLeave}
        onTouchEnd={this._onMouseLeave}
        className={(props.className || '') + ' text_overflow_ellipsis'}
      >
        {props.children}
      </Comp>
    )
  }
}

export default Timing(TextOverflow)
